import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from 'pages/sideBar/SideBar';

const BASE = 'https://backend.washta.com/api/residential';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

// local calendar date — toISOString() is UTC, so just after midnight in UAE/PK it still gave yesterday
const toDateStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const fmtShort = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

const packageStatus = (o) => {
  if (o.status === 'cancelled') return { label: 'Cancelled', bg: '#fee2e2', color: '#991b1b' };
  if (o.status === 'expired') return { label: 'Expired', bg: '#f3f4f6', color: '#6b7280' };
  if (o.status === 'completed' || o.washesCompleted >= o.washCount) return { label: 'Completed', bg: '#d1fae5', color: '#1a6641' };
  if (new Date(o.startDate) > new Date()) return { label: 'Upcoming', bg: '#f0eeff', color: '#6B5FE4' };
  return { label: 'Active', bg: '#FEF3C7', color: '#92400E' };
};

const nextWashDate = (o) => {
  if (o.status !== 'active' || o.washesCompleted >= o.washCount) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const next = (o.washLog || [])
    .filter((w) => w.status === 'scheduled' && new Date(w.scheduledDate) >= today)
    .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate))[0];
  return next ? next.scheduledDate : null;
};

const Dailyops = () => {
  const [washGroups, setWashGroups] = useState([]);
  const [stats, setStats] = useState({ dueToday: 0, completed: 0, pending: 0, locations: 0 });
  const [activeFilter, setActiveFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [allOrders, setAllOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData(currentDate);
    fetchAllOrders();
  }, []);

  const fetchAllOrders = async () => {
    try {
      const res = await axios.get(`${BASE}/seller/orders`, { headers: authHeaders() });
      setAllOrders((res.data.data || []).filter(o => o.billingStatus === 'paid'));
    } catch (err) {
      console.error(err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchData = async (date) => {
    setLoading(true);
    const dateStr = toDateStr(date);
    try {
      const [opsRes, statsRes] = await Promise.all([
        axios.get(`${BASE}/seller/daily-ops?date=${dateStr}`, { headers: authHeaders() }),
        axios.get(`${BASE}/seller/daily-stats?date=${dateStr}`, { headers: authHeaders() }),
      ]);

      const groups = (opsRes.data.data || []).map((g, i) => ({
        id: g.locationId,
        name: g.locationDisplayName,
        sub: `Residential · ${g.orders.length} washes today`,
        dotColor: ['#6B5FE4', '#1a6641', '#888', '#e67e22'][i % 4],
        collapsed: false,
        washes: g.orders.map((o) => ({
          id: o._id,
          orderId: o._id,
          todayWashId: o.todayWashId,
          plate: `${o.emirate || 'AD'} ${o.plateNumber}`,
          car: o.customer?.name || 'Customer',
          color: o.carType,
          type: o.carType,
          pkg: `${o.washCount} / month`,
          days: (o.recurringDays || []).map((d) => d.slice(0, 2)).join('·'),
          done: o.washesCompleted,
          total: o.washCount,
          ends: new Date(o.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
          completed: o.todayWashStatus === 'completed',
        })),
      }));

      setWashGroups(groups);
      setStats(statsRes.data.data || { dueToday: 0, completed: 0, pending: 0, locations: 0 });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const changeDate = (offset) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + offset);
    setCurrentDate(newDate);
    fetchData(newDate);
  };

  const isToday = toDateStr(currentDate) === toDateStr(new Date());
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const prevDayName = (() => { const d = new Date(currentDate); d.setDate(d.getDate() - 1); return dayNames[d.getDay()]; })();
  const nextDayName = (() => { const d = new Date(currentDate); d.setDate(d.getDate() + 1); return dayNames[d.getDay()]; })();
  const currentDayLabel = isToday ? `${dayNames[currentDate.getDay()]} (Today)` : dayNames[currentDate.getDay()];
  const dateLabel = currentDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

  const toggleGroupCollapse = (groupId) => {
    setWashGroups((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, collapsed: !g.collapsed } : g))
    );
  };

  const markComplete = async (groupId, wash) => {
    if (!wash.todayWashId) return;
    try {
      await axios.patch(
        `${BASE}/seller/orders/${wash.orderId}/wash/${wash.todayWashId}/complete`,
        {},
        { headers: authHeaders() }
      );
      setWashGroups((prev) =>
        prev.map((g) =>
          g.id === groupId
            ? { ...g, washes: g.washes.map((w) => w.id === wash.id ? { ...w, completed: true, done: w.done + 1 } : w) }
            : g
        )
      );
      setStats((prev) => ({ ...prev, completed: prev.completed + 1, pending: Math.max(0, prev.pending - 1) }));
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to mark complete');
    }
  };

  const totalDue = stats.dueToday;
  const totalCompleted = stats.completed;
  const totalPending = stats.pending;

  const filteredGroups = washGroups
    .filter((g) => activeFilter === 'All' || g.name.includes(activeFilter))
    .map((g) => ({
      ...g,
      washes: g.washes.filter((w) =>
        w.plate.toLowerCase().includes(search.toLowerCase())
      ),
    }));

  return (
    <div className="mainDiv-all-sideBar">
      <div className="div-sidebar">
        <Sidebar />
      </div>
      <div className="div-Dashbaord dailyops-page" style={{padding:0}}>
        <div className="dashboard">
          <div className="styles-div-order">
            <div className="admin-shell">
              <div className="ops-topbar">
                <div>
                  <div className="h1-all-heading">Today's Residential Washes</div>
                  <div className="mp-subtitle">
                    {dateLabel} · {totalDue} washes due across {stats.locations} locations
                  </div>
                </div>
                <div className="day-nav">
                  <div className="day-btn" onClick={() => changeDate(-1)}>← {prevDayName}</div>
                  <div className={`day-btn ${isToday ? 'today' : ''}`}>{currentDayLabel}</div>
                  <div className="day-btn" onClick={() => changeDate(1)}>{nextDayName} →</div>
                </div>
              </div>

              <div className="stat-row">
                <div className="stat-card">
                  <div className="stat-val">{loading ? '...' : totalDue}</div>
                  <div className="stat-label">Due today</div>
                </div>
                <div className="stat-card">
                  <div className="stat-val green">{loading ? '...' : totalCompleted}</div>
                  <div className="stat-label">Completed</div>
                </div>
                <div className="stat-card">
                  <div className="stat-val amber">{loading ? '...' : totalPending}</div>
                  <div className="stat-label">Pending</div>
                </div>
                <div className="stat-card">
                  <div className="stat-val" style={{ color: '#888', fontSize: 18 }}>
                    {loading ? '...' : stats.locations}
                  </div>
                  <div className="stat-label">Locations</div>
                </div>
              </div>

              <div className="filter-section">
                <div className="filter-row">
                  <span className="filter-label">Location:</span>
                  <div
                    className={`loc-chip ${activeFilter === 'All' ? 'active' : ''}`}
                    onClick={() => setActiveFilter('All')}
                  >
                    All
                  </div>
                  {washGroups.map((g) => {
                    const shortName = g.name.split(' — ')[0];
                    return (
                      <div
                        key={g.id}
                        className={`loc-chip ${activeFilter === shortName ? 'active' : ''}`}
                        style={g.washes.length === 0 ? { color: '#ccc' } : {}}
                        onClick={() => setActiveFilter(shortName)}
                      >
                        {shortName} <strong>{g.washes.length}</strong>
                      </div>
                    );
                  })}
                  <input
                    className="filter-search"
                    placeholder="🔍  Search plate…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>

              <div className="loc-groups">
                {loading ? (
                  <div style={{ padding: '30px', textAlign: 'center', color: '#aaa', fontSize: 12 }}>Loading washes...</div>
                ) : filteredGroups.length === 0 ? (
                  <div style={{ padding: '30px', textAlign: 'center', color: '#6b7280', fontSize: 14 }}>No washes scheduled for this day.</div>
                ) : filteredGroups.map((g) => {
                  const doneCount = g.washes.filter((w) => w.completed).length;
                  const pendingCount = g.washes.length - doneCount;
                  return (
                    <div key={g.id} className={`loc-group ${g.collapsed ? 'collapsed' : ''}`}>
                      <div
                        className="loc-group-header"
                        onClick={() => toggleGroupCollapse(g.id)}
                      >
                        <div className="loc-group-left">
                          <div className="loc-dot" style={{ background: g.dotColor }}></div>
                          <div>
                            <div className="loc-group-name">{g.name}</div>
                            <div className="loc-group-sub">{g.sub}</div>
                          </div>
                        </div>
                        <div className="loc-group-counts">
                          {doneCount > 0 && (
                            <span className="count-badge count-done">✓ {doneCount} done</span>
                          )}
                          {pendingCount > 0 && (
                            <span className="count-badge count-pending">{pendingCount} pending</span>
                          )}
                          <span className="loc-chevron">{g.collapsed ? '▼ Expand' : '▲'}</span>
                        </div>
                      </div>

                      {!g.collapsed && g.washes.length > 0 && (
                        <table className="wash-table">
                          <thead>
                            <tr>
                              <th>Plate</th>
                              <th>Vehicle</th>
                              <th>Type</th>
                              <th>Package</th>
                              <th>Days</th>
                              <th>Done / Total</th>
                              <th>Ends</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.washes.map((w) => (
                              <tr key={w.id}>
                                <td>
                                  <span className="plate-badge">{w.plate}</span>
                                </td>
                                <td>
                                  <div className="car-cell">
                                    <span className="car-name">{w.car}</span>
                                    <span className="car-sub">{w.color}</span>
                                  </div>
                                </td>
                                <td>
                                  <span className="type-tag">{w.type}</span>
                                </td>
                                <td>
                                  <span className="pkg-tag">{w.pkg}</span>
                                </td>
                                <td>
                                  <span style={{ fontSize: 10, color: '#6B5FE4', fontWeight: 600 }}>
                                    {w.days}
                                  </span>
                                </td>
                                <td>
                                  <span className="washes-left">
                                    {w.done} / {w.total}
                                  </span>
                                </td>
                                <td>
                                  <span style={{ fontSize: 10, color: '#888' }}>{w.ends}</span>
                                </td>
                                <td>
                                  {w.completed ? (
                                    <span className="action-completed">✓ Done</span>
                                  ) : (
                                    <span
                                      className="action-done"
                                      onClick={() => markComplete(g.id, w)}
                                    >
                                      ✔ Complete
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })}
                </div>
            </div>

            {/* ---------- ALL PACKAGES ---------- */}
            <div className="admin-shell" style={{ marginTop: 16 }}>
              <div style={{ padding: '16px 22px 0' }}>
                <div className="h1-all-heading">All Packages</div>
                <div className="mp-subtitle" style={{ marginBottom: 14 }}>Active, upcoming, completed and expired packages for your locations</div>
              </div>
              {ordersLoading ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#aaa', fontSize: 12 }}>Loading packages...</div>
              ) : allOrders.length === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>No packages found.</div>
              ) : (
                <table className="wash-table" style={{ margin: '0 0 16px' }}>
                  <thead>
                    <tr>
                      <th>Plate</th>
                      <th>Vehicle</th>
                      <th>Location</th>
                      <th>Package</th>
                      <th>Days</th>
                      <th>Done / Total</th>
                      <th>Next wash</th>
                      <th>Period</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allOrders.map((o) => {
                      const st = packageStatus(o);
                      const next = nextWashDate(o);
                      return (
                      <tr key={o._id}>
                        <td><span className="plate-badge">{o.emirate || 'AD'} {o.plateNumber}</span></td>
                        <td>
                          <div className="car-cell">
                            <span className="car-name">{o.customerId?.fullName || 'Customer'}</span>
                            <span className="car-sub">{o.carType}</span>
                          </div>
                        </td>
                        <td><span style={{ fontSize: 10, color: '#888' }}>{o.locationDisplayName}</span></td>
                        <td><span className="pkg-tag">{o.washCount} / month</span></td>
                        <td><span style={{ fontSize: 10, color: '#6B5FE4', fontWeight: 600 }}>{(o.recurringDays || []).map(d => d.slice(0,2)).join('·')}</span></td>
                        <td><span className="washes-left">{o.washesCompleted} / {o.washCount}</span></td>
                        <td><span style={{ fontSize: 10, color: next ? '#111' : '#bbb' }}>{next ? fmtShort(next) : '—'}</span></td>
                        <td><span style={{ fontSize: 10, color: '#888' }}>{fmtShort(o.startDate)} – {fmtShort(o.endDate)}</span></td>
                        <td><span className="count-badge" style={{ background: st.bg, color: st.color }}>{st.label}</span></td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* ---------- Scoped CSS ---------- */}
      <style>{`
        .dailyops-page .admin-shell{overflow:hidden;padding:12px 0px}
        .dailyops-page .topbar{display:flex;align-items:center;justify-content:space-between;padding:12px 20px;background:#f7f7f9;border-bottom:1px solid #eee;}
        .dailyops-page .logo{font-size:15px;font-weight:700;color:#111;letter-spacing:-.02em;}
        .dailyops-page .logo span{color:#6B5FE4;}
        .dailyops-page .breadcrumb{font-size:11px;color:#aaa;}
        .dailyops-page .topbar-date{font-size:11px;color:#6B5FE4;font-weight:600;background:#f0eeff;padding:4px 10px;border-radius:6px;}

        .dailyops-page .ops-topbar{padding:16px 22px 0;display:flex;align-items:flex-start;justify-content:space-between;gap:16px;}
        .dailyops-page .ops-title{font-size:15px;font-weight:700;color:#111;}
        .dailyops-page .ops-sub{font-size:11px;color:#888;margin-top:2px;}

        .dailyops-page .stat-row{display:flex;gap:10px;padding:14px 22px;}
        .dailyops-page .stat-card{flex:1;background:#f7f7f9;border:1px solid #eee;border-radius:10px;padding:24px 14px;}
        .dailyops-page .stat-val{font-size:22px;font-weight:700;color:#6B5FE4;line-height:1;}
        .dailyops-page .stat-val.green{color:#1a6641;}
        .dailyops-page .stat-val.amber{color:#92400E;}
        .dailyops-page .stat-label{font-size:10px;color:#888;}

        .dailyops-page .filter-section{padding:0 22px 14px;}
        .dailyops-page .filter-row{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:10px;}
        .dailyops-page .filter-label{font-size:10px;font-weight:700;color:#aaa;text-transform:uppercase;letter-spacing:.06em;white-space:nowrap;}
        .dailyops-page .loc-chip{font-size:10px;padding:5px 12px;border:1px solid #ddd;border-radius:100px;color:#888;cursor:pointer;white-space:nowrap;}
        .dailyops-page .loc-chip.active{background:#6B5FE4;color:#fff;border-color:#6B5FE4;}
        .dailyops-page .filter-search{margin-left:auto;font-size:11px;border:1px solid #ddd;border-radius:7px;padding:5px 10px;background:#fff;color:#111;font-family:inherit;width:160px;}

        .dailyops-page .day-nav{display:flex;align-items:center;gap:6px;}
        .dailyops-page .day-btn{font-size:10px;padding:5px 10px;border:1px solid #ddd;border-radius:6px;color:#888;cursor:pointer;}
        .dailyops-page .day-btn.today{background:#6B5FE4;color:#fff;border-color:#6B5FE4;font-weight:700;}

        .dailyops-page .loc-groups{padding:0 22px 20px;display:flex;flex-direction:column;gap:14px;}
        .dailyops-page .loc-group{border:1px solid #e8e8ec;border-radius:12px;overflow:hidden;}
        .dailyops-page .loc-group-header{display:flex;align-items:center;justify-content:space-between;padding:10px 16px;background:#f7f7f9;border-bottom:1px solid #eee;cursor:pointer;}
        .dailyops-page .loc-group.collapsed .loc-group-header{border-bottom:none;}
        .dailyops-page .loc-group-left{display:flex;align-items:center;gap:10px;}
        .dailyops-page .loc-dot{width:8px;height:8px;border-radius:50%;background:#6B5FE4;flex-shrink:0;}
        .dailyops-page .loc-group-name{font-size:12px;font-weight:700;color:#111;}
        .dailyops-page .loc-group-sub{font-size:10px;color:#888;}
        .dailyops-page .loc-group-counts{display:flex;align-items:center;gap:8px;}
        .dailyops-page .count-badge{font-size:10px;font-weight:700;padding:3px 9px;border-radius:100px;}
        .dailyops-page .count-pending{background:#FEF3C7;color:#92400E;}
        .dailyops-page .count-done{background:#d1fae5;color:#1a6641;}
        .dailyops-page .loc-chevron{font-size:12px;color:#ccc;}

        .dailyops-page .wash-table{width:100%;border-collapse:collapse;}
        .dailyops-page .wash-table th{font-size:9px;font-weight:700;color:#aaa;text-transform:uppercase;letter-spacing:.06em;padding:8px 14px;text-align:left;border-bottom:1px solid #eee;background:#fafafa;}
        .dailyops-page .wash-table td{font-size:11px;color:#111;padding:9px 14px;border-bottom:1px solid #f5f5f5;vertical-align:middle;}
        .dailyops-page .wash-table tr:last-child td{border-bottom:none;}
        .dailyops-page .wash-table tr:hover td{background:#fafafa;}

        .dailyops-page .plate-badge{display:inline-block;background:#111;color:#fff;font-size:10px;font-weight:800;padding:3px 9px;border-radius:5px;letter-spacing:.05em;font-family:monospace;}
        .dailyops-page .car-cell{display:flex;flex-direction:column;gap:1px;}
        .dailyops-page .car-name{font-size:11px;font-weight:600;color:#111;}
        .dailyops-page .car-sub{font-size:9px;color:#aaa;}
        .dailyops-page .washes-left{font-size:11px;font-weight:700;color:#6B5FE4;}
        .dailyops-page .pkg-tag{font-size:9px;color:#4a3db8;background:#f0eeff;padding:2px 7px;border-radius:4px;font-weight:600;}
        .dailyops-page .type-tag{font-size:9px;color:#1a6641;background:#e0f2ea;padding:2px 7px;border-radius:4px;font-weight:600;}

        .dailyops-page .action-done{font-size:10px;font-weight:700;padding:5px 14px;background:#1a6641;color:#fff;border-radius:100px;cursor:pointer;white-space:nowrap;}
        .dailyops-page .action-completed{font-size:10px;font-weight:700;padding:5px 14px;background:#d1fae5;color:#1a6641;border-radius:100px;white-space:nowrap;border:1px solid #6ee7b7;}
      `}</style>
    </div>
  );
};

export default Dailyops;